// ─────────────────────────────────────────────────────────────────────────────
// RightPanel.jsx  —  Detail / Chat / Alerts / Messages right column
//
// Used on both desktop (fixed 320px right column) and mobile (full-screen view).
// Contains 4 sub-components defined in this file for easy co-location:
//   VehicleDetailCard — load stats, progress bar, actions, history
//   AlertsList        — system alert cards
//   MessagesList      — driver message preview list
//   ChatPanel         — full dispatcher ↔ driver chat
//
// WHAT TO EDIT HERE:
//   Tab labels     → find the ["detail","chat","alerts","messages"] array
//   Chat bubbles   → find bg-gray-100 (dispatcher) and bg-blue-600 (driver)
//   Action buttons → find the grid-cols-4 action row inside VehicleDetailCard
//   Alert icons    → find ⚠️ and ℹ️ in AlertsList
//   History dots   → real GPS breadcrumb data, passed in as the `history` prop (see DispatcherOverview.jsx gpsHistory)
//
// PROPS:
//   rightTab       {string}   "detail" | "chat" | "alerts" | "messages"
//   setRightTab    {fn}
//   selectedLoad   {object}
//   selectedDriver {object}
//   detailOpen     {boolean}
//   setDetailOpen  {fn}
//   simRunning     {boolean}
//   simSpeed       {number}
//   openGPSRecall  {fn}
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useRef, useEffect } from "react";

// Small relative/clock time helper — replaces the old constants.js `tnow()`
// mock (which always returned the same fixed string) now that timestamps
// come from real backend data.
function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// ── Emoji picker ─────────────────────────────────────────────────────────────
// Plain-text unicode emoji, no external library/font needed — renders using
// whatever emoji font the OS already has. WhatsApp-style: a row of category
// tabs (one representative emoji as the tab icon) switches which grid is
// shown, so ~90 emoji are reachable without one giant unsorted wall.
const EMOJI_CATEGORIES = [
  { key: "smileys", tabIcon: "😀", label: "Smileys", items: [
    "😀","😁","😂","🤣","😊","😇","🙂","🙃","😉","😍","😘","😋","😜","🤪","😎","🥳",
    "😢","😭","😤","😠","😡","🤬","😱","😨","😴","🥱","🤔","🤗","🙄","😳","🥵","🥶",
  ]},
  { key: "gestures", tabIcon: "👍", label: "Gestures", items: [
    "👍","👎","👋","🙏","👏","🤝","💪","✌️","🤞","👌","🤙","☝️","👉","👈","🖐️","✋",
    "❤️","🧡","💛","💚","💙","💜","🖤","🤍","💯","🔥","✨","⭐","💥","💤","👀","🎉",
  ]},
  { key: "logistics", tabIcon: "🚚", label: "Logistics", items: [
    "🚚","🚛","🚗","🚨","📍","🗺️","🧭","⏰","⏱️","🛑","🏁","⛽","🔧","🔩","📦","🚧",
    "🌧️","☀️","⛅","❄️","🌙","🌡️","🌪️","🌫️","📞","📱","✅","❌","⚠️","❗","❓","🆗",
  ]},
];

function EmojiPicker({ onSelect, onClose }) {
  const ref = useRef(null);
  const [activeCategory, setActiveCategory] = useState(EMOJI_CATEGORIES[0].key);
  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [onClose]);

  const active = EMOJI_CATEGORIES.find(c => c.key === activeCategory) || EMOJI_CATEGORIES[0];

  return (
    <div ref={ref} className="absolute bottom-full left-0 mb-2 w-72 bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden z-20">
      {/* Category tabs */}
      <div className="flex items-center gap-0.5 px-2 pt-2 pb-1 border-b border-gray-100 bg-gray-50">
        {EMOJI_CATEGORIES.map(cat => (
          <button
            key={cat.key}
            type="button"
            title={cat.label}
            onClick={() => setActiveCategory(cat.key)}
            className={`flex-1 text-lg leading-none py-1.5 rounded-lg transition-colors ${
              activeCategory === cat.key ? "bg-white shadow-sm ring-1 ring-gray-200" : "opacity-50 hover:opacity-90"
            }`}
          >
            {cat.tabIcon}
          </button>
        ))}
      </div>
      {/* Grid for the selected category */}
      <div className="grid grid-cols-8 gap-0.5 p-2 max-h-48 overflow-y-auto custom-scrollbar">
        {active.items.map((emoji, i) => (
          <button
            key={`${emoji}-${i}`}
            type="button"
            onClick={() => onSelect(emoji)}
            className="text-lg leading-none py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Shared status pill ────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const map = {
    "In Progress": "bg-blue-100 text-blue-700",
    "Pending":     "bg-orange-100 text-orange-600",
    "Completed":   "bg-green-100 text-green-700",
    "Delayed":     "bg-red-100 text-red-600",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-semibold whitespace-nowrap ${map[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

// ── Vehicle Detail Card ───────────────────────────────────────────────────────
function VehicleDetailCard({ load, driver, simRunning, simSpeed, onClose, onGPSRecall, history }) {
  if (!load) return <div className="p-4 text-sm text-gray-400">Select a load to view details.</div>;
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mx-3 mt-3 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <span className="font-semibold text-sm text-gray-800">Vehicle Detail</span>
        <div className="flex items-center gap-1">
          {/* <button className="w-7 h-7 hover:bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
            </svg>
          </button> */}
          <button onClick={onClose} className="w-7 h-7 hover:bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xl leading-none">×</button>
        </div>
      </div>
      <div className="p-4">
        {/* Load header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-14 h-11 bg-gray-100 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">🚛</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-sm text-gray-900">PLU-01-{load.id.slice(-4)}</span>
              <StatusPill status={load.status}/>
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-amber-400 text-xs">★</span>
              <span className="text-xs font-semibold text-gray-700">{driver?.rating || "—"}</span>
              <span className="text-xs text-gray-400 ml-1">{load.driver}</span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5 truncate">{load.origin} → {load.destination}</p>
            <p className="text-xs text-gray-400">ETA {load.eta} · {load.remaining} remaining</p>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mb-3">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{width:`${load.progress}%`}}/>
          </div>
          <p className="text-xs text-gray-500 mt-1">{load.progress}%</p>
        </div>
        {/* Action buttons — add/remove icons here */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
            "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
            "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
          ].map((d, i) => (
            <button key={i} className="h-9 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d={d}/>
              </svg>
            </button>
          ))}
          {/* <button className="h-9 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 font-bold text-base transition-colors">···</button> */}
        </div>
        {/* Stats row */}
        <div className="grid grid-cols-4 gap-2 mb-4 py-3 border-t border-b border-gray-100">
          {[
            {lbl:"SPEED",   val: simRunning ? `${simSpeed} mph` : (load.speed != null ? `${load.speed} mph` : "—")},
            {lbl:"UPDATED", val: load.lastUpdate || "—"},
            {lbl:"FUEL",    val: load.fuel != null ? `${load.fuel}%` : "N/A"},
            {lbl:"TEMP",    val: load.temp != null ? load.temp : "N/A"},
          ].map(s => (
            <div key={s.lbl} className="text-center">
              <p className="text-[9px] text-gray-400 font-medium tracking-wider">{s.lbl}</p>
              <p className="text-xs font-bold text-gray-800 mt-0.5">{s.val}</p>
            </div>
          ))}
        </div>
        {/* History timeline — real GPS breadcrumb data from the `history` prop */}
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <span className="text-sm font-semibold text-gray-800">Recent History</span>
            <button onClick={onGPSRecall} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
              📡 GPS Recall
            </button>
          </div>
          <div className="space-y-2.5">
            {(!history || history.length === 0) && (
              <p className="text-xs text-gray-400">No GPS history yet for this load.</p>
            )}
            {(history || []).map((h, i) => (
              <div key={i} className="flex items-center gap-3 text-xs">
                <span className="text-gray-400 font-mono text-[10px] w-9 flex-shrink-0">{h.time}</span>
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${h.dot==="green"?"bg-green-500":h.dot==="blue"?"bg-blue-500":h.dot==="amber"?"bg-amber-400":"bg-gray-300"}`}/>
                <span className="font-semibold text-gray-700 w-16 flex-shrink-0">{h.status}</span>
                <span className="text-gray-400 truncate">{h.place}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Alerts List ───────────────────────────────────────────────────────────────
// `alerts` comes from GET /api/loads/dispatcher/alerts/ — items look like
// {id, level: "warning"|"info", title, detail, at}.
function AlertsList({ alerts = [], onViewAll }) {
  return (
    <div className="px-3 py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-sm text-gray-800">Alerts</span>
        <button onClick={onViewAll} className="text-xs text-blue-600 font-medium hover:text-blue-800">View all</button>
      </div>
      <div className="space-y-2">
        {alerts.length === 0 && <p className="text-xs text-gray-400">No active alerts.</p>}
        {alerts.slice(0, 5).map((a) => (
          <div key={a.id} className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-gray-100">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${a.level==="warning" ? "bg-orange-100" : "bg-blue-100"}`}>
              <span className="text-sm">{a.level === "warning" ? "⚠️" : "ℹ️"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800 leading-snug">{a.title}</p>
              <p className="text-xs text-gray-400 mt-0.5">{a.detail}</p>
            </div>
            <span className="text-[10px] text-gray-300 flex-shrink-0 whitespace-nowrap">{fmtTime(a.at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Messages List ─────────────────────────────────────────────────────────────
// Built from the real assignments list (`loads`) rather than a mock array —
// there's no separate "conversation preview" endpoint on the backend, but
// LoadAssignmentSerializer already includes `unread_count` per assignment,
// which is enough to build a real conversation list. Selecting a row opens
// that load's chat (real history + WebSocket), same as the "Chat" tab.
function MessagesList({ loads = [], onOpenChat }) {
  const conversations = loads.filter(l => l.status !== "Pending");
  return (
    <div className="px-3 py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-sm text-gray-800">Messages</span>
      </div>
      <div className="space-y-2">
        {conversations.length === 0 && <p className="text-xs text-gray-400">No active conversations.</p>}
        {conversations.map((l) => {
          const unread = l.raw?.unread_count || 0;
          const initials = (l.driver || "?").split(" ").map(p => p[0]).slice(0,2).join("").toUpperCase();
          return (
            <button key={l.rawId} onClick={() => onOpenChat(l)} className="w-full flex items-center gap-2.5 bg-white rounded-xl p-3 border border-gray-100 hover:border-blue-200 transition-colors text-left">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-sm flex-shrink-0">{initials}</div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800">{l.driver}</p>
                <p className="text-xs text-gray-400 truncate mt-0.5">{l.id} · {l.status}</p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-[10px] text-gray-300">{l.lastUpdate}</span>
                {unread > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-blue-500 text-white text-[10px] leading-4 text-center font-semibold">{unread}</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Chat Panel ────────────────────────────────────────────────────────────────
// WhatsApp-style: a horizontal conversation strip up top lets the dispatcher
// jump between drivers with one tap (backed by the /ws/dispatch-hub/ socket
// in DispatcherOverview.jsx, so unread dots update live even for
// conversations you haven't opened). Quick-reply chips send common
// load-status updates instantly. Every message here is already scoped to one
// Load (each conversation = one LoadAssignment room), so it's implicitly
// "tagged by Load ID" — there's no cross-load inbox to confuse it with.
const QUICK_REPLIES = ["Arrived", "Delayed", "Rest Stop", "Police Check"];

// Read-receipt ticks — single grey check while a message is still
// optimistic/pending, double grey once the server has confirmed it landed,
// double blue/colored once the driver has actually opened the chat and
// read it (fed by the "read_receipt" WebSocket event — see
// DispatcherOverview.jsx). Same visual language as WhatsApp/OfferUp.
function ReadTicks({ pending, read }) {
  if (pending) {
    return (
      <svg className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" viewBox="0 0 16 16" fill="none">
        <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  return (
    <svg className={`w-4 h-3.5 flex-shrink-0 ${read ? "text-blue-500" : "text-gray-300"}`} viewBox="0 0 20 16" fill="none">
      <path d="M1 8.5l3.5 3.5L11 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7.5 8.5l3.5 3.5L18 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ChatPanel({ driver, load, messages = [], chatInput = "", setChatInput, onSendMessage, currentUserId, conversations = [], onSwitchConversation, chatConnected, driverTyping }) {
  const endRef = useRef(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  useEffect(() => { endRef.current?.scrollIntoView({behavior:"smooth"}); }, [messages]);
  useEffect(() => { setEmojiOpen(false); }, [load?.rawId]); // close the picker when switching conversations

  const isMine = (m) => {
    const senderId = typeof m.sender === "object" ? m.sender?.id : m.sender;
    return senderId != null && String(senderId) === String(currentUserId);
  };

  const initials = (load?.driver || driver?.name || "?").split(" ").map(p => p[0]).slice(0,2).join("").toUpperCase();
  const activeConversations = conversations.filter(c => c.status !== "Pending");

  return (
    <div className="flex flex-col h-full">
      {/* Conversation switcher — tap an avatar to jump chats instantly */}
      {activeConversations.length > 1 && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 overflow-x-auto flex-shrink-0 bg-white">
          {activeConversations.map(c => {
            const active = c.rawId === load?.rawId;
            const unread = c.raw?.unread_count || 0;
            const ini = (c.driver || "?").split(" ").map(p=>p[0]).slice(0,2).join("").toUpperCase();
            return (
              <button key={c.rawId} onClick={() => onSwitchConversation?.(c)}
                className="relative flex-shrink-0 flex flex-col items-center gap-1 w-12" title={c.driver}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs border-2 transition-colors ${
                  active ? "bg-blue-600 text-white border-blue-600" : "bg-blue-50 text-blue-700 border-transparent"
                }`}>{ini}</div>
                {unread > 0 && !active && (
                  <span className="absolute -top-0.5 right-0 min-w-[15px] h-[15px] px-1 rounded-full bg-red-500 text-white text-[9px] leading-[15px] text-center font-bold">{unread}</span>
                )}
                <span className={`text-[9px] truncate w-full text-center ${active ? "text-blue-700 font-semibold" : "text-gray-400"}`}>{(c.driver||"").split(" ")[0]}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Chat header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-sm flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{load?.driver || driver?.name || "Driver"}</p>
          <div className="flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${load?.isTrackingActive ? "bg-green-500" : "bg-gray-300"}`}/>
            <span className={`text-xs ${load?.isTrackingActive ? "text-green-600" : "text-gray-400"}`}>
              {load?.isTrackingActive ? "Online" : "Offline"}
            </span>
          </div>
        </div>
        {load?.id && <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-2 py-1 rounded flex-shrink-0">{load.id}</span>}
      </div>
      {/* Live connection status — this is a real WebSocket, not polling: a
          driver's message lands here the instant it's sent. This pill only
          shows if the socket briefly drops (network blip) and is
          auto-reconnecting, so you know if a lag you're seeing is real. */}
      {/* {load && !chatConnected && (
        <div className="flex items-center gap-1.5 px-4 py-1 bg-amber-50 border-b border-amber-100 flex-shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"/>
          <span className="text-[11px] text-amber-700 font-medium">Reconnecting live chat…</span>
        </div>
      )} */}
      {/* Message bubbles */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {!load && <p className="text-sm text-gray-400 text-center mt-6">Select a load to open its chat.</p>}
        {load && messages.length === 0 && <p className="text-sm text-gray-400 text-center mt-6">No messages yet.</p>}
        {messages.map(m => {
          const mine = isMine(m);
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"} ${m.pending ? "opacity-60" : ""}`}>
              <div className={`flex flex-col ${mine ? "items-end" : "items-start"} max-w-[80%]`}>
                {!mine && <span className="text-[10px] text-gray-400 px-1 mb-0.5">{m.sender_name}</span>}
                {/* gray = dispatcher (sent by me), blue = driver (received) */}
                <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  mine
                    ? "bg-gray-100 text-gray-800 rounded-br-sm"
                    : "bg-blue-600 text-white rounded-bl-sm"
                }`}>{m.content}</div>
                <span className="text-[10px] text-gray-300 mt-1 px-1 flex items-center gap-1">
                  {fmtTime(m.created_at)}
                  {mine && <ReadTicks pending={!!m.pending} read={!!m.is_read} />}
                </span>
              </div>
            </div>
          );
        })}
        {load && driverTyping && (
          <div className="flex justify-start">
            <div className="bg-blue-50 text-blue-500 px-3 py-2 rounded-2xl rounded-bl-sm flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}/>
              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}/>
              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}/>
            </div>
          </div>
        )}
        <div ref={endRef}/>
      </div>
      {/* Quick replies */}
      {load && (
        <div className="flex items-center gap-1.5 px-3 pt-2 overflow-x-auto flex-shrink-0">
          {QUICK_REPLIES.map(q => (
            <button key={q} onClick={() => onSendMessage(null, q)}
              className="flex-shrink-0 text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600 whitespace-nowrap transition-colors">
              {q}
            </button>
          ))}
        </div>
      )}
      {/* Input */}
      <div className="px-3 py-3 border-t border-gray-100 flex-shrink-0 relative">
        {emojiOpen && (
          <EmojiPicker
            onSelect={(emoji) => setChatInput((chatInput || "") + emoji)}
            onClose={() => setEmojiOpen(false)}
          />
        )}
        <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 bg-gray-50">
          <button
            type="button"
            onClick={() => setEmojiOpen(v => !v)}
            disabled={!load}
            title="Emoji"
            className="text-lg leading-none text-gray-400 hover:text-gray-600 disabled:opacity-40 flex-shrink-0"
          >
            🙂
          </button>
          <input
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") onSendMessage(e); }}
            placeholder={load ? "Type a message..." : "Select a load first..."}
            disabled={!load}
            className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-300 outline-none min-w-0"
          />
          <button
            onClick={onSendMessage}
            disabled={!chatInput.trim() || !load}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors flex-shrink-0"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

// ── RightPanel (exported) ─────────────────────────────────────────────────────
export default function RightPanel({
  rightTab, setRightTab,
  selectedLoad, selectedDriver,
  detailOpen, setDetailOpen,
  simRunning, simSpeed, openGPSRecall,
  messages, chatInput, setChatInput, onSendMessage,
  alerts, gpsHistory, currentUserId,
  loads, onSelectLoad,
  chatConnected, driverTyping,
}) {
  return (
    <>
      {/* Tab bar — change tab labels in the array below */}
      <div className="flex border-b border-gray-200 bg-white flex-shrink-0 sticky top-0 z-10">
        {["detail","chat","alerts","messages"].map(t => (
          <button key={t} onClick={() => setRightTab(t)}
            className={`flex-1 py-3 text-xs font-semibold capitalize transition-colors border-b-2 ${
              rightTab === t ? "border-blue-600 text-blue-700" : "border-transparent text-gray-400 hover:text-gray-700"
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Detail tab */}
      {rightTab === "detail" && (
        <div className="overflow-y-auto flex-1">
          {detailOpen && (
            <VehicleDetailCard load={selectedLoad} driver={selectedDriver}
              simRunning={simRunning} simSpeed={simSpeed} history={gpsHistory}
              onClose={() => setDetailOpen(false)} onGPSRecall={openGPSRecall}/>
          )}
          {!detailOpen && (
            <div className="p-3">
              <button onClick={() => setDetailOpen(true)} className="text-sm text-blue-600 hover:underline">
                Show vehicle detail
              </button>
            </div>
          )}
          <AlertsList alerts={alerts} onViewAll={() => setRightTab("alerts")}/>
          <MessagesList loads={loads} onOpenChat={(l) => { onSelectLoad?.(l); setRightTab("chat"); }}/>
        </div>
      )}

      {/* Chat tab */}
      {rightTab === "chat" && (
        <div className="flex-1 flex flex-col overflow-hidden">
          <ChatPanel driver={selectedDriver} load={selectedLoad}
            messages={messages} chatInput={chatInput} setChatInput={setChatInput}
            onSendMessage={onSendMessage} currentUserId={currentUserId}
            conversations={loads} onSwitchConversation={onSelectLoad}
            chatConnected={chatConnected} driverTyping={driverTyping}/>
        </div>
      )}

      {/* Alerts tab */}
      {rightTab === "alerts" && (
        <div className="overflow-y-auto flex-1">
          <div className="p-3 space-y-2">
            <h3 className="font-semibold text-sm text-gray-800 mb-3">All Alerts</h3>
            {(!alerts || alerts.length === 0) && <p className="text-xs text-gray-400">No active alerts.</p>}
            {(alerts || []).map((a) => (
              <div key={a.id} className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-gray-100">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${a.level==="warning"?"bg-orange-100":"bg-blue-100"}`}>
                  <span className="text-sm">{a.level==="warning"?"⚠️":"ℹ️"}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800">{a.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{a.detail}</p>
                </div>
                <span className="text-[10px] text-gray-300 flex-shrink-0">{fmtTime(a.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages tab */}
      {rightTab === "messages" && (
        <div className="overflow-y-auto flex-1">
          <MessagesList loads={loads} onOpenChat={(l) => { onSelectLoad?.(l); setRightTab("chat"); }}/>
        </div>
      )}
    </>
  );
}